import { put, takeLatest, select, call } from "redux-saga/effects";
import {
  fetchExistingUserApiCall,
  getCsrfTokensApiCall,
  getCustomerDetails,
  sendOTP,
  userSignUp,
  validateEmailApiCall,
  validatePasswordLoginApiCall,
  loginWithPasswordApiCall,
} from "../../../api/loginApi";
import {
  USE_PASSWORD_LOGIN,
  USE_OTP_LOGIN,
  LOGGED_IN_SUCCESSFULLY,
  INVALID_PASSWORD,
} from "../../../constants/pageConstants/loginConstants";
import {
  changePhoneNumber,
  customerDetailsError,
  otpError,
  requestToFetchSignUp,
  requestToFetchUsl,
  requestToGetCsrfTokens,
  requestToGetCustomerDetails,
  requestToHandleSuccessfullToast,
  requestToOpenLoginDrawer,
  requestToOTP,
  requestToRegister,
  requestToSaveUserDetails,
  requestToSendOtp,
  requestToValidateEmail,
  requestPasswordLogin,
  passwordLoginSuccess,
  passwordLoginError,
  requestToShowPasswordLogin,
  requestToInitiateOtpLogin,
  clearPasswordLoginError,
  responseFromGetCustomerDetails,
  setCsrfTokens,
  setEmailValidationResponse,
  setValidateExistingUser,
  signUpError,
  uslError,
} from "../../slices/loginSignupSlice";
import { initiateFusCall } from "../commonSaga";
import { ACCOUNT_CREATED_SUCCESSFULLY, OTP_MEDIUM } from "../../../constants/pageConstants/loginConstants";

/**
 * Sends OTP for an existing user (login flow).
 * @param {*} userData
 */
function* sendOtpForExistingUser(userData) {
  const otpTokenA = yield select((state) => state.loginSignUp.tokens?._fc_a);
  const otpTokenB = yield select((state) => state.loginSignUp.tokens?._fc_b);

  let isInternationalPhoneNumber = false;
  const countryCode = userData?.countryCode?.trim();
  if (countryCode && countryCode !== "91" && countryCode !== "+91") {
    isInternationalPhoneNumber = true;
  }

  const otpPayload = {
    un: userData?.un,
    logintoken: userData?.logintoken,
    _fc_a: otpTokenA,
    _fc_b: otpTokenB,
    deviceType: userData?.deviceType,
    requestedOtpMedium: userData?.phoneNumber ? (isInternationalPhoneNumber ? OTP_MEDIUM.EMAIL : "") : OTP_MEDIUM.EMAIL,
  };
  const otpResponse = yield sendOTP(otpPayload);
  if (otpResponse?.response === "success") {
    yield put(requestToOTP(otpResponse));
  } else {
    yield put(otpError(otpResponse));
  }
}

/**
 *
 * @param {*} action
 */
function* fetchUser(userData) {
  try {
    if (userData?.exists) {
      yield put(
        requestToSaveUserDetails({
          email: userData?.email,
          isExisting: true,
          loginToken: userData?.logintoken,
          phoneNumber: userData?.phoneNumber,
        }),
      );

      if (USE_PASSWORD_LOGIN) {
        yield put(requestToShowPasswordLogin(true));
        return;
      }

      if (USE_OTP_LOGIN) {
        yield call(sendOtpForExistingUser, userData);
      }
    } else if (!userData?.exists) {
      yield put(
        requestToSaveUserDetails({
          email: userData?.email,
          isExisting: false,
          loginToken: userData?.logintoken,
        }),
      );
      yield put(requestToRegister(true));
    }
  } catch (error) {
    yield put(uslError(error));
  }
}
/**
 *
 * @param {*} action
 */
function* sendOtpToUser(action) {
  try {
    const response = yield sendOTP(action.payload);
    if (response?.response === "success") {
      yield put(requestToOTP(response));
      yield put(requestToRegister(false));
      yield put(changePhoneNumber(false));
    }
  } catch (error) {
    yield put(otpError(error));
  }
}

/**
 *
 * @param {*} action
 */
function* fetchSignUpData(action) {
  const { loginToken, email, userName, countryCode, phoneNumber, isMobile, handleRedirection } = action.payload;
  try {
    const response = yield userSignUp(loginToken, userName, countryCode, phoneNumber, email);

    if (response.isSuccess) {
      if (!isMobile) {
        yield call(initiateFusCall);
      }
      yield put(requestToHandleSuccessfullToast(ACCOUNT_CREATED_SUCCESSFULLY));
      yield put(requestToOpenLoginDrawer(false));
      if (handleRedirection) handleRedirection();
    }
  } catch (error) {
    yield put(signUpError(error));
  }
}
/**
 *
 * @param {*} action
 */
function* fetchCsrfTokens() {
  try {
    const response = yield getCsrfTokensApiCall();
    if (response.isSuccess) {
      yield put(setCsrfTokens(response?.data));
    }
  } catch (error) {
    yield put(signUpError(error));
  }
}
/**
 *
 * @param {*} action
 */
function* validateExistingUser(action) {
  try {
    const response = yield fetchExistingUserApiCall({ un: action.payload.un });
    if (response) {
      yield fetchUser({ ...response, ...action.payload });
      yield put(setValidateExistingUser(response));
    }
  } catch (error) {
    yield put(signUpError(error));
  }
}

/**
 * To fetch the login user details.
 * @returns
 */
export function* getCustomerLoginDetails() {
  try {
    const response = yield getCustomerDetails();
    yield put(responseFromGetCustomerDetails(response));
  } catch (error) {
    yield put(customerDetailsError(error));
  }
}

/**
 * Validates email format using server-side API
 * @param {*} action
 */
function* validateEmailSaga(action) {
  try {
    const response = yield validateEmailApiCall(action.payload.email);
    yield put(setEmailValidationResponse(response));

    if (response?.isEmailValid) {
      // If email is valid, proceed with existing user validation
      yield call(validateExistingUser, action);
    }
    // If email is invalid, the component will handle showing the error
  } catch (error) {
    yield put(signUpError(error));
  }
}

/**
 * Switches from password screen to OTP flow for an existing user.
 * @param {*} action
 */
function* initiateOtpLoginSaga(action) {
  if (!USE_OTP_LOGIN) {
    return;
  }
  try {
    yield put(requestToShowPasswordLogin(false));
    yield put(clearPasswordLoginError());
    yield call(sendOtpForExistingUser, action.payload);
  } catch (error) {
    yield put(otpError(error));
  }
}

/**
 * Validates password via qcl-rj, then completes login via lu-rj when valid.
 * @param {*} action
 */
function* passwordLoginSaga(action) {
  const { un, pw, externalLoginKey, handleRedirection } = action.payload;
  try {
    const validateResponse = yield validatePasswordLoginApiCall({ un, pw });

    if (validateResponse?.valid) {
      const loginResponse = yield loginWithPasswordApiCall({ un, pw, externalLoginKey });

      if (loginResponse?.response === "error" || loginResponse?.valid === false) {
        yield put(passwordLoginError(loginResponse?.response?.msg || INVALID_PASSWORD));
        return;
      }

      yield call(initiateFusCall);
      yield put(passwordLoginSuccess());
      yield put(requestToHandleSuccessfullToast(LOGGED_IN_SUCCESSFULLY));
      yield put(requestToOpenLoginDrawer(false));
      if (handleRedirection) {
        handleRedirection();
      }
    } else {
      yield put(passwordLoginError(INVALID_PASSWORD));
    }
  } catch (error) {
    yield put(passwordLoginError(error?.message || INVALID_PASSWORD));
  }
}

/**
 * Description placeholder
 *
 * @export
 * @returns {{}}
 */
function* watcherLoginFlowSaga() {
  yield takeLatest(requestToFetchUsl.type, fetchUser);
  yield takeLatest(requestToSendOtp.type, sendOtpToUser);
  yield takeLatest(requestToFetchSignUp.type, fetchSignUpData);
  yield takeLatest(requestToGetCsrfTokens.type, fetchCsrfTokens);
  yield takeLatest(requestToValidateEmail.type, validateEmailSaga);
  yield takeLatest(requestToGetCustomerDetails.type, getCustomerLoginDetails);
  yield takeLatest(requestPasswordLogin.type, passwordLoginSaga);
  yield takeLatest(requestToInitiateOtpLogin.type, initiateOtpLoginSaga);
}

export default watcherLoginFlowSaga;
